import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import SessionManager from '../../../services/SessionManager';
import { apiFetch } from '../../../utils/apiFetch';
import {
  PREMIUM_PLAN_NAME,
  UPGRADE_ROUTE,
  getSessionExpirationDate,
  getTierConfig,
  isPremiumTier,
  isSessionExpired
} from '../../../config/entitlements';
import { resolveTierFromUser } from '../../../utils/runtimeTier';
import './SessionControls.css';

/**
 * SessionControls Component - Enhanced Session Management UI
 * 
 * This component provides improved controls for managing battle sessions,
 * including saving, loading, overwriting, and session metadata management.
 * 
 * Features:
 * - Save session with overwrite option
 * - Load existing sessions
 * - Delete sessions
 * - Session management UI
 * - Storage usage indicator
 */
const SessionControls = ({ 
  onSave, 
  onLoad, 
  onOverwrite,
  onGoLive,
  isGM,
  className
}) => {
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [storageInfo, setStorageInfo] = useState({
    used: 0,
    available: 5 * 1024 * 1024, // Typical browser limit is 5MB
    percentage: 0
  });
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  // Multiplayer Sending Stone (Battle Room)
  // date_of_change: 2025-09-25 - Expose copyable Sending Stone when in multiplayer session
  const [inviteLink, setInviteLink] = useState('');
  const [liveSessionId, setLiveSessionId] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteState, setInviteState] = useState(null);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [goingLive, setGoingLive] = useState(false);
  const tier = resolveTierFromUser(user);
  const tierConfig = getTierConfig(tier);
  const isPremium = isPremiumTier(tier);
  const sessionLimit = tierConfig.limits.savedSessions;

  // Load sessions and storage info
  const refreshSessions = () => {
    const allSessions = SessionManager.getAllSessions();
    setSessions(allSessions);
    
    // Calculate storage usage
    try {
      // Get all localStorage keys
      const keys = Object.keys(localStorage);
      let totalSize = 0;
      
      // Calculate total size
      keys.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) {
          totalSize += new TextEncoder().encode(value).length;
        }
      });
      
      // Update storage info
      setStorageInfo({
        used: totalSize,
        available: 5 * 1024 * 1024, // Typical browser limit (5MB)
        percentage: Math.min(100, Math.round((totalSize / (5 * 1024 * 1024)) * 100))
      });
    } catch (error) {
      console.error('[SessionControls] Error calculating storage usage:', error);
    }
  };

  // Initial load
  useEffect(() => {
    if (isOpen) {
      refreshSessions();
    }
  }, [isOpen]);

  // Compute Sending Stone from URL when dropdown opens
  useEffect(() => {
    if (!isOpen) return;
    try {
      const path = window.location.pathname || '';
      const match = path.match(/\/(?:battle|battleroom)\/([^/?#]+)/);
      if (match && match[1]) {
        const link = `${window.location.origin}/battle/${match[1]}`;
        setInviteLink(link);
        setLiveSessionId(match[1]);
      } else {
        setInviteLink('');
        setLiveSessionId('');
      }
    } catch (e) {
      setInviteLink('');
      setLiveSessionId('');
    }
  }, [isOpen]);

  const handleInviteSubmit = async (event) => {
    event.preventDefault();

    const normalizedEmail = String(inviteEmail || '').trim().toLowerCase();
    if (!normalizedEmail || !liveSessionId || !isGM || isSendingInvite) {
      return;
    }

    setIsSendingInvite(true);
    setInviteState(null);

    try {
      const inviterName = user?.fullName
        || user?.firstName
        || user?.username
        || user?.emailAddresses?.[0]?.emailAddress
        || 'Game Master';

      const response = await apiFetch(`/api/combat/${liveSessionId}/invite`, {
        method: 'POST',
        body: JSON.stringify({
          email: normalizedEmail,
          inviterName,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to send invite');
      }

      setInviteEmail('');
      const emailDeliveryStatus = payload?.emailDelivery?.status;
      const inviteMessage = emailDeliveryStatus === 'sent'
        ? `Invite sent to ${normalizedEmail}.`
        : `Invite saved for ${normalizedEmail}. Email delivery is not available yet, but the player will still see it when signed in.`;
      setInviteState({
        tone: emailDeliveryStatus === 'failed' ? 'warning' : 'success',
        message: inviteMessage,
      });
    } catch (error) {
      setInviteState({
        tone: 'error',
        message: error?.message || 'Failed to send invite.',
      });
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleGoLiveClick = async () => {
    if (!onGoLive || goingLive) return;
    setGoingLive(true);
    try {
      await onGoLive();
    } finally {
      setGoingLive(false);
    }
  };

  const handleSaveClick = () => {
    if (!isPremium && sessionLimit !== 'unlimited' && sessions.length >= sessionLimit) {
      return;
    }
    setIsOpen(false);
    onSave();
    // Refresh the session list after a short delay
    setTimeout(refreshSessions, 500);
  };

  const handleSaveWithOverwriteClick = () => {
    setIsOpen(false);
    onOverwrite(); // This will call handleSaveSession with overwrite=true
    // Refresh the session list after a short delay
    setTimeout(refreshSessions, 500);
  };

  const handleLoadClick = (sessionId) => {
    const selected = sessions.find((session) => session.id === sessionId);
    if (selected && isSessionExpired(selected)) {
      alert(`"${selected.name}" has gone cold. Clear it out or upgrade to ${PREMIUM_PLAN_NAME} to keep long-running campaigns alive.`);
      return;
    }
    setIsOpen(false);
    onLoad(sessionId);
  };

  const handleDeleteClick = (e, sessionId) => {
    e.stopPropagation(); // Prevent triggering row click (load)
    
    if (window.confirm("Are you sure you want to delete this battle room save?")) {
      SessionManager.deleteSession(sessionId);
      refreshSessions();
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) {
      return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  };

  return (
    <div className={`session-controls ${className || ''}`}>
      <button
        className="session-toggle-button"
        onClick={() => setIsOpen(!isOpen)}
      >
        Room Options
      </button>

      {isOpen && (
        <div className="session-dropdown">
          <div className="session-dropdown-header">
            <div>
              <h3>Room Options</h3>
              <p className="session-plan-copy">
                {isPremium
                  ? `${PREMIUM_PLAN_NAME}: unlimited saved battle rooms, no expiry timer.`
                  : 'Free loadout: 1 saved battle room at a time. It fades after 30 days unless you overwrite or delete it.'}
              </p>
            </div>
            <button
              type="button"
              className="session-close-button"
              aria-label="Close room options"
              onClick={() => setIsOpen(false)}
            >
              ×
            </button>
          </div>

          {/* Multiplayer Invite (shown when in a live /battle/:id URL) */}
          {inviteLink ? (
            <div className="multiplayer-invite">
              <h4>Invite Players</h4>
              <p className="invite-copy">
                Share the room link or send a quieter invite directly to a player&apos;s email.
              </p>
              <div className="invite-row">
                <input
                  type="text"
                  value={inviteLink}
                  readOnly
                  onClick={(e) => e.target.select()}
                />
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(inviteLink);
                      alert('Sending Stone copied!');
                    } catch (e) {
                      prompt('Copy this link:', inviteLink);
                    }
                  }}
                >
                  Copy
                </button>
              </div>
              {isGM && (
                <form className="email-invite-form" onSubmit={handleInviteSubmit}>
                  <label className="email-invite-label" htmlFor="battle-room-email-invite">
                    Send by email
                  </label>
                  <div className="invite-row invite-row--email">
                    <input
                      id="battle-room-email-invite"
                      type="email"
                      value={inviteEmail}
                      onChange={(event) => setInviteEmail(event.target.value)}
                      placeholder="player@example.com"
                      autoComplete="email"
                    />
                    <button type="submit" disabled={isSendingInvite || !inviteEmail.trim()}>
                      {isSendingInvite ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                  {inviteState && (
                    <p className={`invite-feedback invite-feedback--${inviteState.tone}`}>
                      {inviteState.message}
                    </p>
                  )}
                </form>
              )}
            </div>
          ) : isGM && onGoLive ? (
            <div className="go-live-block">
              <h4>Live Battle Room</h4>
              <p className="go-live-copy">This battle room is local-only. Go Live to open a shareable room that players can join.</p>
              <button
                className="go-live-button"
                onClick={handleGoLiveClick}
                disabled={goingLive}
              >
                {goingLive ? 'Opening…' : 'Go Live'}
              </button>
            </div>
          ) : null}

          <div className="session-actions">
            <button
              onClick={handleSaveClick}
              disabled={!isPremium && sessionLimit !== 'unlimited' && sessions.length >= sessionLimit}
            >
              Save Room
            </button>
            <button onClick={handleSaveWithOverwriteClick} disabled={sessions.length === 0}>Save &amp; Overwrite</button>
            {!isPremium && (
              <button
                type="button"
                className="session-upgrade-button"
                onClick={() => {
                  window.location.assign(UPGRADE_ROUTE);
                }}
              >
                Unlock {PREMIUM_PLAN_NAME}
              </button>
            )}
          </div>

          {!isPremium && (
            <div className="session-tier-lock">
              <div>
                <strong>Encounter Presets</strong>
                <p>Bank reusable ambushes, boss rooms, and full encounter loadouts in {PREMIUM_PLAN_NAME}.</p>
              </div>
              <button type="button" disabled>
                Premium Only
              </button>
            </div>
          )}

          <div className="storage-info">
            <div className="storage-bar">
              <div
                className="storage-used"
                style={{ width: `${storageInfo.percentage}%` }}
              />
            </div>
            <div className="storage-text">
              {formatSize(storageInfo.used)} / {formatSize(storageInfo.available)} used
              ({storageInfo.percentage}%)
            </div>
            {!isPremium && (
              <div className="storage-tier-text">
                Save slot: {Math.min(sessions.length, sessionLimit)} / {sessionLimit}
              </div>
            )}
          </div>

          <div className="session-list">
            <h4>Saved Battle Rooms</h4>
            {sessions.length > 0 ? (
              <div className="session-table">
                <div className="session-table-header">
                  <div className="session-name">Name</div>
                  <div className="session-date">Created</div>
                  <div className="session-actions">Actions</div>
                </div>
                {sessions.map(session => (
                  <div
                    key={session.id}
                    className={`session-row ${selectedSessionId === session.id ? 'selected' : ''}`}
                    onClick={() => setSelectedSessionId(session.id)}
                    onDoubleClick={() => handleLoadClick(session.id)}
                  >
                    <div className="session-name">{session.name}</div>
                    <div className="session-date">
                      {isSessionExpired(session)
                        ? 'Expired'
                        : getSessionExpirationDate(session)
                          ? `Until ${formatDate(getSessionExpirationDate(session))}`
                          : formatDate(session.createdAt)}
                    </div>
                    <div className="session-actions">
                      <button
                        onClick={() => handleLoadClick(session.id)}
                        disabled={isSessionExpired(session)}
                        title={isSessionExpired(session) ? `Upgrade to ${PREMIUM_PLAN_NAME} or clear this slot.` : 'Load battle room'}
                      >
                        {isSessionExpired(session) ? 'Expired' : 'Load'}
                      </button>
                      <button onClick={(e) => handleDeleteClick(e, session.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-sessions">
                {isPremium ? 'No saved battle rooms found' : 'No saved battle rooms found. Your free slot is ready for one battle room save.'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionControls;
